import React, { useState } from 'react';
import Swal from "sweetalert2";
import { Row, Col } from "reactstrap";
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { displayErrorToast, displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import { updateMoisture, getMoistureDetails, getMoistureDailyCount, getMcNumber, printMoistureCorrection } from "../../services/MoistureCorrectionServices";
import { getUserAllowedPlants } from '../../services/PlantServices';
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { applyCorrectionFactors, mapMoistureApiToInputs, MOISTURE_MATERIALS } from './MoistureCorrectionUtils';
import { confirmMoistureSoftLimit, formatMoistureDailyCount } from '../../utils/moistureCorrectionUtils';
import { getCurrentDate, getCurrentTime } from '../../utils/DateUtils';
import { openPdfResponse } from '../../utils/openPdfInApp';

function MoistureCorrectionEdit() {
  const [error] = useState({});
  const [isSubmitting] = useState(true);
  const [cookies] = useCookies(['myToken']);
  const [inProgress, setInProgress] = useState(false);
  const [inputs, setInputs] = useState({});
  const [plants, setPlants] = useState([]);
  const [dailyCountMeta, setDailyCountMeta] = useState(null);
  const [canUpdateInPlace, setCanUpdateInPlace] = useState(false);
  const originalMoistureRef = React.useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    try {
      if (name === 'is_active') {
        setInputs((values) => ({ ...values, is_active: value === 'true' }));
        return;
      }
      if (name === 'mc_date' || name === 'mc_time') {
        const updated = { ...inputs, [name]: value };
        setInputs(updated);
        if (updated.plant_id && !canUpdateInPlace) {
          getMcNumber(cookies, parseInt(updated.plant_id, 10), updated.mc_date, updated.mc_time)
            .then((meta) => setInputs((prev) => ({
              ...prev,
              mc_no: meta.mc_no,
              mc_date: meta.mc_date,
              mc_time: (meta.mc_time || '').slice(0, 5),
            })))
            .catch(() => {});
        }
        return;
      }
      const updates = { [name]: value };
      if (name.startsWith('moisture_')) {
        const material = name.replace('moisture_', '');
        const absorption = parseFloat(inputs[`absorption_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (absorption - parseFloat(value || 0)).toFixed(2);
      } else if (name.startsWith('absorption_')) {
        const material = name.replace('absorption_', '');
        const moisture = parseFloat(inputs[`moisture_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (parseFloat(value || 0) - moisture).toFixed(2);
      }
      setInputs((values) => ({ ...values, ...updates }));
    } catch (e) {
      displayErrorToast(e);
    }
  };

  const buildMoisturePayload = () => {
    const fields = applyCorrectionFactors({
      id: inputs.id,
      plant_id: parseInt(inputs.plant_id, 10),
      moisture_msand: inputs.moisture_msand ? parseFloat(inputs.moisture_msand) : 0,
      moisture_csand: inputs.moisture_csand ? parseFloat(inputs.moisture_csand) : 0,
      moisture_rsand: inputs.moisture_rsand ? parseFloat(inputs.moisture_rsand) : 0,
      moisture_12mm: inputs.moisture_12mm ? parseFloat(inputs.moisture_12mm) : 0,
      moisture_20mm: inputs.moisture_20mm ? parseFloat(inputs.moisture_20mm) : 0,
      absorption_msand: inputs.absorption_msand ? parseFloat(inputs.absorption_msand) : 0,
      absorption_csand: inputs.absorption_csand ? parseFloat(inputs.absorption_csand) : 0,
      absorption_rsand: inputs.absorption_rsand ? parseFloat(inputs.absorption_rsand) : 0,
      absorption_12mm: inputs.absorption_12mm ? parseFloat(inputs.absorption_12mm) : 0,
      absorption_20mm: inputs.absorption_20mm ? parseFloat(inputs.absorption_20mm) : 0,
    });
    return {
      ...fields,
      plant: fields.plant_id,
      mc_date: inputs.mc_date,
      mc_time: inputs.mc_time,
      is_active: inputs.is_active !== false,
      correction_factor_msand: parseFloat(fields.correction_factor_msand) || 0,
      correction_factor_csand: parseFloat(fields.correction_factor_csand) || 0,
      correction_factor_rsand: parseFloat(fields.correction_factor_rsand) || 0,
      correction_factor_12mm: parseFloat(fields.correction_factor_12mm) || 0,
      correction_factor_20mm: parseFloat(fields.correction_factor_20mm) || 0,
    };
  };

  const isStatusOnlyChange = () => {
    if (!originalMoistureRef.current) {
      return false;
    }
    const original = originalMoistureRef.current;
    const moistureFields = [
      'moisture_msand', 'moisture_csand', 'moisture_rsand', 'moisture_12mm', 'moisture_20mm',
      'absorption_msand', 'absorption_csand', 'absorption_rsand', 'absorption_12mm', 'absorption_20mm',
    ];
    const valuesChanged = moistureFields.some((field) => (
      parseFloat(inputs[field] || 0) !== parseFloat(original[field] || 0)
    ));
    const dateChanged = (inputs.mc_date || '') !== (original.mc_date || '');
    const timeChanged = (inputs.mc_time || '').slice(0, 5) !== (original.mc_time || '').slice(0, 5);
    const statusChanged = (inputs.is_active !== false) !== (original.is_active !== false);
    return statusChanged && !valuesChanged && !dateChanged && !timeChanged;
  };

  const saveMoistureCorrection = () => {
    setInProgress(true);
    updateMoisture(cookies, buildMoisturePayload(), id)
      .then((response) => {
        setInProgress(false);
        const updatedInPlace = response?.updated_in_place ?? canUpdateInPlace;
        const saveMode = response?.save_mode;
        let message = 'New moisture correction recorded.';
        if (saveMode === 'status_update') {
          message = `Status updated to ${inputs.is_active === false ? 'Inactive' : 'Active'}.`;
        } else if (updatedInPlace) {
          message = `Moisture correction ${inputs.mc_no || ''} updated.`;
        }
        Swal.fire('Saved!', message, 'success');
        navigate('/MoistureCorrectionTable');
      })
      .catch((error) => {
        setInProgress(false);
        displayError(error.response?.data, 'Save Failed');
      });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (!inputs.plant_id) {
        Swal.fire('Please select a plant.', '', 'info');
        return;
      }
      if (!((Object.values(error).every((x) => !x)) && isSubmitting)) {
        Swal.fire('Please check * marked fields are filled and details given are valid...', '', 'info');
        return;
      }
      if (canUpdateInPlace) {
        Swal.fire({
          title: 'Update this moisture correction?',
          html: `Values will be updated on <b>${inputs.mc_no || 'this MC'}</b>.<br/>No delivery challan is linked yet.`,
          showCancelButton: true,
          confirmButtonText: 'Yes, update',
          cancelButtonText: 'No',
        }).then((result) => {
          if (result.isConfirmed) {
            saveMoistureCorrection();
          } else if (result.isDismissed) {
            Swal.fire('Not saved', '', 'info');
          }
        });
        return;
      }
      if (isStatusOnlyChange()) {
        Swal.fire({
          title: inputs.is_active === false ? 'Deactivate this MC?' : 'Activate this MC?',
          html: inputs.is_active === false
            ? 'This MC will no longer appear for new delivery challans. Linked DCs keep their existing batch report.'
            : 'This MC will be available for new delivery challans.',
          showCancelButton: true,
          confirmButtonText: 'Yes, update status',
          cancelButtonText: 'No',
        }).then((result) => {
          if (result.isConfirmed) {
            saveMoistureCorrection();
          } else if (result.isDismissed) {
            Swal.fire('Not saved', '', 'info');
          }
        });
        return;
      }
      const canProceed = await confirmMoistureSoftLimit(
        cookies,
        parseInt(inputs.plant_id, 10),
        'save a new correction'
      );
      if (!canProceed) {
        return;
      }
      Swal.fire({
        title: 'Save as new correction?',
        html: 'This MC is already linked to delivery challan(s). A new moisture correction record will be created. Previous values are kept in history.',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then((result) => {
        if (result.isConfirmed) {
          saveMoistureCorrection();
        } else if (result.isDismissed) {
          Swal.fire('Not saved', '', 'info');
        }
      });
    } catch (e) {
      displayErrorToast(e);
    }
  };

  const onPrintMoistureCorrection = async () => {
    try {
      const blob = await printMoistureCorrection(cookies, id);
      openPdfResponse(blob, {
        title: 'Moisture Correction Report',
        downloadFileName: `moisture_correction_${id}.pdf`,
      });
    } catch (error) {
      let message = error?.response?.data ?? error.message;
      if (message instanceof Blob) {
        try {
          const text = await message.text();
          message = JSON.parse(text);
        } catch {
          message = 'PDF generation failed on server.';
        }
      }
      displayError(message, 'Print Failed');
    }
  };

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = (plantList.plant_list || [])
          .filter((obj) => obj.status)
          .map((plant) => ({ value: plant.id, label: plant.plant_alias || plant.name }));
        setPlants(plantOptions);
      })
      .catch((err) => displayError(err.response?.data ?? err.message, 'Failed to load plants'));
  }, [cookies]);

  React.useEffect(() => {
    if (!id) {
      return;
    }
    setInProgress(true);
    getMoistureDetails(cookies, id)
      .then((response) => {
        setInProgress(false);
        const linkedDcCount = Number(response.delivery_challan_count ?? response.delivery_challans?.length ?? 0);
        const updateInPlace = response.can_update_in_place ?? linkedDcCount === 0;
        setCanUpdateInPlace(updateInPlace);
        const loadedInputs = mapMoistureApiToInputs({
          ...response,
          mc_date: response.mc_date || getCurrentDate(),
          mc_time: (response.mc_time || getCurrentTime()).slice(0, 5),
        });
        originalMoistureRef.current = loadedInputs;
        setInputs(loadedInputs);
        if (response.plant_id) {
          getMoistureDailyCount(cookies, response.plant_id)
            .then(setDailyCountMeta)
            .catch(() => setDailyCountMeta(null));
        }
      })
      .catch((error) => {
        setInProgress(false);
        displayError(error.response?.data, "Loading Failed");
      });
  }, [id, cookies]);

  const plantLabel = plants.find((p) => String(p.value) === String(inputs.plant_id))?.label
    || inputs.plant_name
    || '';

  return (
    <>
      <div id="outer-container" className="App">
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <ErrorToast />
        <LoadingOverlay inProgress={inProgress} />
        <div id="page-wrap">
          <form onSubmit={handleSubmit}>
            <div className="premium-page-header-wrap">
              <PremiumPageHeader
                module="QUALITY CONTROL"
                title={canUpdateInPlace ? 'Edit Moisture Correction' : 'Moisture Correction'}
                subtitle={canUpdateInPlace
                  ? 'No delivery challan is linked yet. Saving updates this MC number in place. CF% = Absorption − Moisture.'
                  : 'Linked to delivery challan(s). Change status only, or save new values as a new MC record.'}
                modeLabel={canUpdateInPlace ? 'EDIT MODE' : 'NEW VERSION'}
              />
              {!canUpdateInPlace && dailyCountMeta && (
                <p className="text-center mb-0 moisture-correction-count" style={{ color: dailyCountMeta.soft_limit_reached ? '#b45309' : '#1e40af', fontWeight: 600 }}>
                  {formatMoistureDailyCount(dailyCountMeta)}
                  {dailyCountMeta.soft_limit_reached ? ' — additional saves allowed with confirmation' : ''}
                </p>
              )}
            </div>
            <FloatingControls
              tableLink="/MoistureCorrectionTable"
              formLink="/MoistureCorrection"
              addNewLabel="New Moisture Correction"
              enableCancel={false}
              enablePrint
              onPrint={onPrintMoistureCorrection}
            />
            <div className="container-fluid">
              <Row>
                <Col xl={12} lg={12} md={12}>
                  <div className="table-bordered shadow p-3 my-2 border-secondary mb-2 bg-blue form-control-panel moisture-correction-panel">
                    <div className="moisture-correction-meta">
                      <div className="moisture-correction-meta-cell">
                        <label>Plant</label>
                        <input
                          className="form-control"
                          value={plantLabel}
                          readOnly
                          style={{ cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="moisture-correction-meta-cell">
                        <label>MC No.</label>
                        <input type="text" className="form-control" value={inputs.mc_no || ''} readOnly style={{ cursor: 'not-allowed' }} />
                      </div>
                      <div className="moisture-correction-meta-cell moisture-correction-meta-cell-datetime">
                        <label htmlFor="mc_date">Date</label>
                        <input type="date" id="mc_date" name="mc_date" className="form-control mandatory-form-control" value={inputs.mc_date || ''} onChange={handleChange} required />
                        <label htmlFor="mc_time">Time</label>
                        <input type="time" id="mc_time" name="mc_time" className="form-control mandatory-form-control" value={inputs.mc_time || ''} onChange={handleChange} required />
                      </div>
                      <div className="moisture-correction-meta-cell">
                        <label htmlFor="is_active">Status</label>
                        <select
                          id="is_active"
                          name="is_active"
                          className="form-control mandatory-form-control"
                          value={inputs.is_active === false ? 'false' : 'true'}
                          onChange={handleChange}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                    </div>

                    <div className="moisture-correction-grid">
                      <span />
                      <h5 className="moisture-correction-head"><b>MOISTURE (%)</b></h5>
                      <h5 className="moisture-correction-head"><b>ABSORPTION (%)</b></h5>
                      <h5 className="moisture-correction-head"><b>CORRECTION FACTOR (%)</b></h5>

                      {MOISTURE_MATERIALS.map(([key, label]) => (
                        <React.Fragment key={key}>
                          <label className="moisture-correction-label">{label}</label>
                          <input
                            type="number"
                            step="any"
                            name={`moisture_${key}`}
                            onChange={handleChange}
                            value={inputs[`moisture_${key}`] || 0}
                            max="15"
                            min="0"
                            className="form-control mandatory-form-control"
                            required
                          />
                          <input
                            type="number"
                            step="any"
                            name={`absorption_${key}`}
                            onChange={handleChange}
                            value={inputs[`absorption_${key}`] || 0}
                            max="5"
                            min="0"
                            className="form-control mandatory-form-control"
                            required
                          />
                          <input
                            type="number"
                            name={`correction_factor_${key}`}
                            value={inputs[`correction_factor_${key}`] || 0}
                            className="form-control"
                            readOnly
                            style={{ cursor: "not-allowed" }}
                            required
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default MoistureCorrectionEdit;
